import React, { useCallback, useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Mail, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import Captcha from "@/components/forms/Captcha";
import api from "@/lib/api";

/**
 * <EmailOtpVerify /> — Email OTP step of the registration wizard.
 *
 *   Step 1: Email + hCaptcha → POST /api/auth/email/send-otp
 *   Step 2: 6-digit OTP       → POST /api/auth/email/verify-otp
 *                             → onVerified({email, verification_token})
 *
 * Props:
 *   onVerified  ({email, verification_token}) => void
 *   defaultEmail  string — prefill (e.g. from a previous attempt)
 */
export default function EmailOtpVerify({ onVerified, defaultEmail = "" }) {
  const [step, setStep] = useState("email");     // 'email' | 'otp'
  const [email, setEmail] = useState(defaultEmail);
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [captchaToken, setCaptchaToken] = useState(null);
  // Honeypot — invisible to humans, irresistible to bots.
  const [website, setWebsite] = useState("");
  const captchaRef = useRef(null);

  // Resend countdown — 30s between sends.
  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setInterval(() => setResendIn((n) => Math.max(0, n - 1)), 1000);
    return () => clearInterval(id);
  }, [resendIn]);

  const validEmail = /^\S+@\S+\.\S+$/.test(email);
  const canSend = validEmail && !sending && !!captchaToken;

  const sendOtp = useCallback(async () => {
    if (!validEmail) return toast.error("Please enter a valid email address.");
    if (!captchaToken) return toast.error("Please complete the captcha.");
    setSending(true);
    try {
      await api.post("/auth/email/send-otp", {
        email: email.toLowerCase().trim(),
        hcaptcha_token: captchaToken,
        website,
      });
      toast.success("Verification code sent — check your inbox.");
      setStep("otp");
      setResendIn(30);
      // Force a captcha refresh so the same token can't be re-used for resend.
      captchaRef.current?.reset();
      setCaptchaToken(null);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not send the verification email.";
      toast.error(detail);
      captchaRef.current?.reset();
      setCaptchaToken(null);
    } finally {
      setSending(false);
    }
  }, [email, captchaToken, website, validEmail]);

  const verifyOtp = useCallback(async (e) => {
    e?.preventDefault?.();
    if (!/^\d{6}$/.test(code)) return toast.error("Enter the 6-digit code.");
    setVerifying(true);
    try {
      const res = await api.post("/auth/email/verify-otp", {
        email: email.toLowerCase().trim(),
        code,
      });
      toast.success("Email verified!");
      onVerified?.({
        email: res.data.email,
        verification_token: res.data.verification_token,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Incorrect or expired code.";
      toast.error(detail);
    } finally {
      setVerifying(false);
    }
  }, [code, email, onVerified]);

  const resend = useCallback(async () => {
    if (resendIn > 0) return;
    if (!captchaToken) {
      toast.error("Please complete the captcha to resend.");
      return;
    }
    await sendOtp();
  }, [resendIn, captchaToken, sendOtp]);

  return (
    <div className="space-y-4" data-testid="email-otp-verify">
      {step === "email" ? (
        <>
          <div>
            <Label className="text-xs">Email address</Label>
            <Input
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value.trim().toLowerCase())}
              className="mt-1.5"
              data-testid="email-otp-input"
              autoComplete="email"
            />
          </div>
          {/* Honeypot — must remain empty for humans */}
          <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: "1px", height: "1px", overflow: "hidden" }}>
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              data-testid="email-otp-honeypot"
            />
          </div>
          <Captcha
            ref={captchaRef}
            onToken={setCaptchaToken}
            testId="email-otp-captcha"
          />
          <Button
            type="button"
            onClick={sendOtp}
            disabled={!canSend}
            className="w-full h-11 bg-[#1F4E3D] hover:bg-[#2E5D4E] text-white gap-2"
            data-testid="email-otp-send-button"
          >
            {sending
              ? (<><Loader2 size={16} className="animate-spin" /> Sending…</>)
              : (<><Mail size={16}/> Send verification code</>)
            }
          </Button>
        </>
      ) : (
        <form onSubmit={verifyOtp} className="space-y-4">
          <button
            type="button"
            onClick={() => { setStep("email"); setCode(""); }}
            className="inline-flex items-center gap-1 text-xs text-[#7A5A22] hover:text-[#1F4E3D]"
            data-testid="email-otp-back-button"
          >
            <ArrowLeft size={12}/> Change email
          </button>
          <div>
            <Label className="text-xs">Enter the 6-digit code sent to <span className="font-medium text-[#111827]">{email}</span></Label>
            <Input
              inputMode="numeric"
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              maxLength={6}
              className="text-center tracking-[0.5em] text-xl font-medium mt-1.5"
              data-testid="email-otp-code-input"
              autoFocus
              autoComplete="one-time-code"
            />
          </div>
          <Button
            type="submit"
            disabled={verifying || code.length < 6}
            className="w-full h-11 bg-[#C9A86A] hover:bg-[#D7BC86] text-[#111827] gap-2"
            data-testid="email-otp-verify-button"
          >
            {verifying
              ? (<><Loader2 size={16} className="animate-spin" /> Verifying…</>)
              : (<><CheckCircle2 size={16}/> Verify email</>)
            }
          </Button>
          <div className="text-xs text-center text-[#4B5563]">
            {resendIn > 0 ? (
              <span data-testid="email-resend-countdown">Resend in {resendIn}s</span>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Captcha
                  ref={captchaRef}
                  onToken={setCaptchaToken}
                  testId="email-otp-resend-captcha"
                />
                <button
                  type="button"
                  onClick={resend}
                  disabled={!captchaToken}
                  className="text-[#1F4E3D] hover:underline disabled:text-[#9CA3AF] disabled:no-underline"
                  data-testid="email-otp-resend-button"
                >
                  Resend code
                </button>
              </div>
            )}
          </div>
        </form>
      )}
    </div>
  );
}
